import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import axiosInstance from "../../config/axiosInterceptor";

const VideoStorageConfig = () => {
  const [config, setConfig] = useState({
    uploadStrategy: "storedInServer",
    fetchStrategy: "fetchFromServer",
    s3Config: {
      bucketName: "",
      region: "",
      accessKeyId: "",
      secretAccessKey: "",
      cloudfrontUrl: "",
    },
    minioConfig: {
      endpoint: "",
      port: 443,
      useSSL: true,
      accessKey: "",
      secretKey: "",
      bucketName: "",
      cdnUrl: "",
    },
  });
  
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingMinio, setTestingMinio] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMinioPassword, setShowMinioPassword] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/admin/video-storage-config");
      if (response.data.success === 1) {
        setConfig(response.data.details);
      }
    } catch (error) {
      toast.error("Failed to fetch configuration");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate S3 config if using S3
    if (
      (config.uploadStrategy === "storeInS3" || config.uploadStrategy === "storeBoth" || 
       config.uploadStrategy === "storeInS3AndMinio" || config.uploadStrategy === "storeAll" ||
       config.fetchStrategy === "fetchFromS3") &&
      (!config.s3Config.bucketName || !config.s3Config.region || 
       !config.s3Config.accessKeyId || !config.s3Config.secretAccessKey)
    ) {
      toast.error("Please fill all S3 configuration fields when using S3 storage");
      return;
    }

    // Validate MinIO config if using MinIO
    if (
      (config.uploadStrategy === "storeInMinio" || config.uploadStrategy === "storeInS3AndMinio" || 
       config.uploadStrategy === "storeAll" || config.fetchStrategy === "fetchFromMinio") &&
      (!config.minioConfig.endpoint || !config.minioConfig.accessKey || 
       !config.minioConfig.secretKey || !config.minioConfig.bucketName)
    ) {
      toast.error("Please fill all MinIO configuration fields when using MinIO storage");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/admin/video-storage-config/update", config);
      if (response.data.success === 1) {
        toast.success("Configuration updated successfully");
        setConfig(response.data.details);
      } else {
        toast.error(response.data.message || "Failed to update configuration");
      }
    } catch (error) {
      toast.error("Failed to update configuration");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const testS3Connection = async () => {
    try {
      setTesting(true);
      const response = await axiosInstance.post("/admin/video-storage-config/test-s3");
      if (response.data.success === 1) {
        toast.success("S3 connection successful!");
      } else {
        toast.error(response.data.message || "S3 connection failed");
      }
    } catch (error) {
      toast.error("S3 connection test failed");
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  const testMinioConnection = async () => {
    try {
      setTestingMinio(true);
      const response = await axiosInstance.post("/admin/video-storage-config/test-minio");
      if (response.data.success === 1) {
        toast.success("MinIO connection successful!");
      } else {
        toast.error(response.data.message || "MinIO connection failed");
      }
    } catch (error) {
      toast.error("MinIO connection test failed");
      console.error(error);
    } finally {
      setTestingMinio(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleS3ConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      s3Config: {
        ...prev.s3Config,
        [field]: value,
      },
    }));
  };

  const handleMinioConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      minioConfig: {
        ...prev.minioConfig,
        [field]: value,
      },
    }));
  };

  if (loading && !config._id) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Video Storage Configuration</h4>
              <small>Manage video upload and fetch strategies</small>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Upload Strategy */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold">
                    <i className="fas fa-upload mr-2"></i>
                    Upload Strategy
                  </label>
                  <select
                    className="form-control"
                    value={config.uploadStrategy}
                    onChange={(e) => handleInputChange("uploadStrategy", e.target.value)}
                    required
                  >
                    <option value="storedInServer">Store in Server Only (Local Storage)</option>
                    <option value="storeInS3">Store in S3 Only (AWS S3 Bucket)</option>
                    <option value="storeInMinio">Store in MinIO Only (MinIO Object Storage)</option>
                    <option value="storeBoth">Store in Both Server & S3</option>
                    <option value="storeInS3AndMinio">Store in S3 & MinIO (Multi-Cloud)</option>
                    <option value="storeAll">Store in All (Server + S3 + MinIO)</option>
                  </select>
                  <small className="form-text text-muted">
                    Choose where new videos will be stored when uploaded. Multiple storage provides redundancy.
                  </small>
                </div>

                {/* Fetch Strategy */}
                <div className="form-group mb-4">
                  <label className="font-weight-bold">
                    <i className="fas fa-download mr-2"></i>
                    Fetch Strategy
                  </label>
                  <select
                    className="form-control"
                    value={config.fetchStrategy}
                    onChange={(e) => handleInputChange("fetchStrategy", e.target.value)}
                    required
                  >
                    <option value="fetchFromServer">Fetch from Server (Direct URL)</option>
                    <option value="fetchFromS3">Fetch from S3 (Signed URL)</option>
                    <option value="fetchFromMinio">Fetch from MinIO (CDN URL)</option>
                  </select>
                  <small className="form-text text-muted">
                    Choose how videos will be fetched when displaying
                  </small>
                </div>

                {/* S3 Configuration Section */}
                {(config.uploadStrategy === "storeInS3" || config.uploadStrategy === "storeBoth" || 
                  config.uploadStrategy === "storeInS3AndMinio" || config.uploadStrategy === "storeAll" ||
                  config.fetchStrategy === "fetchFromS3") && (
                  <div className="card bg-light mb-4">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fab fa-aws mr-2"></i>
                        AWS S3 Configuration
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Bucket Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.s3Config.bucketName}
                              onChange={(e) => handleS3ConfigChange("bucketName", e.target.value)}
                              placeholder="my-video-bucket"
                              required={config.uploadStrategy === "storeInS3"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Region *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.s3Config.region}
                              onChange={(e) => handleS3ConfigChange("region", e.target.value)}
                              placeholder="us-east-1"
                              required={config.uploadStrategy === "storeInS3"}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Access Key ID *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.s3Config.accessKeyId}
                              onChange={(e) => handleS3ConfigChange("accessKeyId", e.target.value)}
                              placeholder="AKIAIOSFODNN7EXAMPLE"
                              required={config.uploadStrategy === "storeInS3"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Secret Access Key *</label>
                            <div className="input-group">
                              <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                value={config.s3Config.secretAccessKey}
                                onChange={(e) => handleS3ConfigChange("secretAccessKey", e.target.value)}
                                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                                required={config.uploadStrategy === "storeInS3"}
                              />
                              <div className="input-group-append">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  <i className={`fas fa-eye${showPassword ? "-slash" : ""}`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          CloudFront URL (Optional)
                          <i className="fas fa-info-circle ml-2" title="Use CloudFront CDN for faster video delivery"></i>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={config.s3Config.cloudfrontUrl}
                          onChange={(e) => handleS3ConfigChange("cloudfrontUrl", e.target.value)}
                          placeholder="https://d111111abcdef8.cloudfront.net"
                        />
                        <small className="form-text text-muted">
                          If you have CloudFront CDN configured, enter the distribution URL here for faster video delivery
                        </small>
                      </div>

                      <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={testS3Connection}
                        disabled={testing}
                      >
                        {testing ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Test S3 Connection
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* MinIO Configuration Section */}
                {(config.uploadStrategy === "storeInMinio" || config.uploadStrategy === "storeInS3AndMinio" || 
                  config.uploadStrategy === "storeAll" || config.fetchStrategy === "fetchFromMinio") && (
                  <div className="card bg-light mb-4">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-database mr-2"></i>
                        MinIO Object Storage Configuration
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Endpoint *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.endpoint || ""}
                              onChange={(e) => handleMinioConfigChange("endpoint", e.target.value)}
                              placeholder="gcdn.ahamcloudservices.com"
                              required={config.uploadStrategy === "storeInMinio"}
                            />
                            <small className="form-text text-muted">MinIO server endpoint (without http://)</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Port</label>
                            <input
                              type="number"
                              className="form-control"
                              value={config.minioConfig?.port || 443}
                              onChange={(e) => handleMinioConfigChange("port", parseInt(e.target.value))}
                              placeholder="443"
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Use SSL</label>
                            <select
                              className="form-control"
                              value={config.minioConfig?.useSSL ? "true" : "false"}
                              onChange={(e) => handleMinioConfigChange("useSSL", e.target.value === "true")}
                            >
                              <option value="true">Yes (HTTPS)</option>
                              <option value="false">No (HTTP)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Bucket Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.bucketName || ""}
                              onChange={(e) => handleMinioConfigChange("bucketName", e.target.value)}
                              placeholder="masti"
                              required={config.uploadStrategy === "storeInMinio"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>CDN URL (Optional)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.cdnUrl || ""}
                              onChange={(e) => handleMinioConfigChange("cdnUrl", e.target.value)}
                              placeholder="https://cdn.yoursite.com"
                            />
                            <small className="form-text text-muted">Custom CDN URL if configured</small>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Access Key *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={config.minioConfig?.accessKey || ""}
                              onChange={(e) => handleMinioConfigChange("accessKey", e.target.value)}
                              placeholder="image-api-user"
                              required={config.uploadStrategy === "storeInMinio"}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>Secret Key *</label>
                            <div className="input-group">
                              <input
                                type={showMinioPassword ? "text" : "password"}
                                className="form-control"
                                value={config.minioConfig?.secretKey || ""}
                                onChange={(e) => handleMinioConfigChange("secretKey", e.target.value)}
                                placeholder="Your secret key"
                                required={config.uploadStrategy === "storeInMinio"}
                              />
                              <div className="input-group-append">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowMinioPassword(!showMinioPassword)}
                                >
                                  <i className={`fas fa-eye${showMinioPassword ? "-slash" : ""}`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={testMinioConnection}
                        disabled={testingMinio}
                      >
                        {testingMinio ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Testing Connection...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle mr-2"></i>
                            Test MinIO Connection
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Information Alert */}
                <div className="alert alert-info">
                  <h6 className="alert-heading">
                    <i className="fas fa-info-circle mr-2"></i>
                    Configuration Guide
                  </h6>
                  <ul className="mb-0">
                    <li><strong>Store in Server Only:</strong> Videos stored locally on your server</li>
                    <li><strong>Store in S3 Only:</strong> Videos uploaded to AWS S3 bucket only</li>
                    <li><strong>Store in MinIO Only:</strong> Videos uploaded to MinIO object storage only</li>
                    <li><strong>Store in Both (Server & S3):</strong> Videos stored in both server and S3</li>
                    <li><strong>Store in S3 & MinIO:</strong> Videos stored in both cloud storage providers (multi-cloud redundancy)</li>
                    <li><strong>Store in All:</strong> Videos stored in Server + S3 + MinIO (maximum redundancy)</li>
                    <li><strong>Fetch from Server:</strong> All videos served directly from your server</li>
                    <li><strong>Fetch from S3:</strong> All videos served via signed URLs from S3 or CloudFront CDN</li>
                    <li><strong>Fetch from MinIO:</strong> All videos served from MinIO or custom CDN</li>
                    <li><strong>CloudFront/CDN:</strong> Use CDN for global delivery and improved performance</li>
                    <li><strong>Note:</strong> Fetch strategy is global - all videos fetched from same source regardless of storage</li>
                  </ul>
                </div>

                {/* Submit Button */}
                <div className="form-group mb-0">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save Configuration
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-lg ml-2"
                    onClick={fetchConfig}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt mr-2"></i>
                    Refresh
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoStorageConfig;
